import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ZitoFooter from '../components/ZitoFooter';

/**
 * Zitotron Tech LLC — company homepage.
 *
 * Dark-mode, startup-cool, Microsoft-level formal. Product portfolio
 * gets poster cards; Contact section
 * carries the real business info Stripe reviewers look for:
 * formation address, phone, staffed inbox split.
 */

type ProductCard = {
  key: string;
  status: string;
  title: string;
  eyebrow: string;
  summary: string;
  bullets: string[];
  href?: string;
  cta?: string;
  icon?: 'plus';
};

const CALENDLY_URL = 'https://calendly.com/zitotrontech/30min';
const CALENDLY_WIDGET_URL = CALENDLY_URL;
const CALENDLY_SCRIPT_ID = 'calendly-widget-script';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget?: (options: { url: string; parentElement: HTMLElement }) => void;
    };
  }
}

const productCards: ProductCard[] = [
  {
    key: 'agentomatic',
    status: 'Flagship product',
    title: 'AgentOmatic.io',
    eyebrow: 'Agentic Development Environment',
    summary: 'AgentOmatic.io is a cross-platform desktop app for running and coordinating multiple AI coding agents in parallel.',
    bullets: [
      'Launch, monitor, steer, and recover many terminal-based coding agents from one workspace.',
      'Coordinates terminals, browser previews, project files, and multi-monitor workflows.',
      'Supports Claude Code, Codex CLI, Gemini CLI, Aider, and local Ollama models.',
    ],
    href: 'https://agentomatic.io',
    cta: 'Visit AgentOmatic.io',
  },
  {
    key: 'vidaforge',
    status: 'Private pre-launch',
    title: 'VidaForge.ai',
    eyebrow: 'Gamified life execution',
    summary: 'VidaForge.ai turns focused work into visible, compounding wins across life goals, career sprints, health, and finances.',
    bullets: [
      'Pin a North Star plus two co-equal focus goals that stay visible in the header.',
      'Focus Mode, comeback wins, badge ladders, healthy-break rewards, and time proof.',
      'Built for CCNA and cert sprints, net-worth tracking, habits, missions, and daily progress.',
    ],
    href: 'https://vidaforge.ai',
    cta: 'Visit VidaForge.ai',
  },
  {
    key: 'postavo',
    status: 'Working build',
    title: 'Postavo.ai',
    eyebrow: 'Agent-first social command center',
    summary: 'Postavo.ai combines a public builder network, multi-platform posting studio, and agent console in one surface.',
    bullets: [
      'Public feed, profiles, explore, comments, badges, verification, and good-vibes moderation.',
      'Studio mode for cross-posting, scheduling, content libraries, analytics, personas, and accounts.',
      'Agent console for AI providers, local-model content generation, automation, and machine API keys.',
    ],
    href: 'https://postavo.ai',
    cta: 'Visit Postavo.ai',
  },
  {
    key: 'startupagency',
    status: 'Startup agency',
    title: 'Startup Agency',
    eyebrow: 'yourAppName.ai / .com / .io',
    summary: 'A flexible startup-build service for founders who want a real product launched, operated, and improved by an engineering team.',
    bullets: [
      'We build and manage your startup: app, site, production deploy, Stripe, and launch support.',
      'Custom options by need: MVPs, SaaS, internal tools, automation, AI workflows, and growth features.',
      'Ongoing support, unlimited updates, new features, fixes, and product iteration after launch.',
    ],
    href: '#book-call',
    cta: 'Book a call',
    icon: 'plus',
  },
];

const HomePage: React.FC = () => {
  const [agentoOpen, setAgentoOpen] = useState(false);

  useEffect(() => {
    if (window.location.hash !== '#book-call') return undefined;
    const timeoutId = window.setTimeout(() => {
      document.getElementById('book-call')?.scrollIntoView({ block: 'start' });
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <div className="App">
      <header className="hero-section">
        {/* ── Nav ── */}
        <nav className="navbar">
          <div className="nav-brand">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <h1>
                <span style={{ color: '#a78bfa', textShadow: '0 0 10px rgba(167,139,250,0.55)' }}>Zitotron</span>
                <span style={{ color: '#2dd4bf', textShadow: '0 0 10px rgba(45,212,191,0.45)' }}>Tech</span>
              </h1>
            </Link>
          </div>
          <div className="nav-links">
            <a href="#products">Products</a>
            <a href="#services">Services</a>
            <a href="#about">About</a>
            <a href="#book-call" className="nav-book-btn">Book a Call</a>
            <a href="#contact" className="nav-contact-btn">✉ Contact</a>
          </div>
        </nav>

        {/* ── Hero ── */}
        <div className="hero-content">
          <h1 className="hero-title">
            AI orchestration &amp; <span className="accent-teal">real</span> software engineering.
          </h1>
          <p className="hero-subtitle">
            Zitotron Tech LLC builds production-grade AI tooling, execution systems, and
            agent-assisted software products for builders who need leverage without chaos.
          </p>
          <p className="hero-note">
            We&apos;re a small team of ex-FAANG engineers. We don&apos;t practice vibe coding.
            We use AI agents deliberately, alongside manual engineering, code review, and
            hands-on product judgment. Every product is built with accountability, care,
            and a serious commitment to code quality.
          </p>
          <div id="products" className="hero-products">
            <span className="section-label">Product portfolio</span>
            <h2>Startups under the Zitotron Tech umbrella.</h2>
            <p className="section-intro">
              Three products plus one Startup Agency service, one engineering standard:
              focused tools and custom builds that help people ship with more leverage.
            </p>
            <div className="product-poster-grid">
              {productCards.map((product) => (
                <ProductPosterCard key={product.key} product={product} />
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* ── Services ── */}
      <section id="services" className="services-section">
        <div className="container">
          <span className="section-label">Services</span>
          <h2>What we do</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon-text">PROD</div>
              <h3>Product studio</h3>
              <p>We build and operate proprietary software products under the Zitotron Tech umbrella, with launch-ready engineering, billing, deployment, and support paths.</p>
            </div>
            <div className="service-card">
              <div className="service-icon-text">CUST</div>
              <h3>Custom AI &amp; software</h3>
              <p>Bespoke AI systems, multi-agent workflows, internal tooling, and production software engagements. Scoped contracts, delivered fast.</p>
            </div>
            <div className="service-card">
              <div className="service-icon-text">OEM</div>
              <h3>OEM &amp; white-label</h3>
              <p>License AgentOmatic.io or other Zitotron IP under custom commercial, OEM, white-label, or enterprise terms tailored to your deployment.</p>
            </div>
            <div className="service-card">
              <div className="service-icon-text">INT</div>
              <h3>Integrations &amp; consulting</h3>
              <p>AI strategy, orchestration architecture, multi-agent rollouts, and dev-velocity consulting for engineering teams moving to agentic workflows.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className="about-section">
        <div className="container">
          <span className="section-label">About</span>
          <h2>Engineering-first, commercially serious.</h2>
          <div className="about-content">
            <div className="about-text">
              <p>
                Zitotron Tech LLC is a software company building production-grade AI tooling. We ship real, proprietary commercial software (not vibe-coded slop) — backed by a free Community tier for everyone, paid tiers for scale, and custom commercial agreements for OEM, white-label, and enterprise deployments.
              </p>
              <p>
                We treat IP seriously — both ours and yours. Every product we release goes through real QA, versioned release cycles, and signed installers. Our engineering team has FAANG roots.
              </p>
              <div className="stats">
                <div className="stat">
                  <span className="stat-number">Wyoming LLC</span>
                  <span className="stat-label">Formed 2024</span>
                </div>
                <div className="stat">
                  <span className="stat-number">Portland, OR</span>
                  <span className="stat-label">Operations HQ</span>
                </div>
                <div className="stat">
                  <span className="stat-number">D-U-N-S® <span className="sensitive-blur">133649238</span></span>
                  <span className="stat-label">Registered business</span>
                </div>
              </div>

              {/* Quick-access contacts so Stripe reviewers (and humans)
                  don't have to scroll to find owner@ and the phone. */}
              <div style={{
                marginTop: 18, padding: '14px 16px',
                background: 'rgba(10,11,18,0.55)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                gap: '10px 18px',
                fontSize: 13, color: '#a8a4bc',
              }}>
                <div>
                  <div style={{ color: '#6d6880', fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Owner</div>
                  <a href="mailto:owner@zitotrontech.com" style={{ color: '#c084fc', textDecoration: 'none', fontWeight: 700, fontFamily: "'JetBrains Mono', Consolas, monospace", fontSize: 13 }}>owner@zitotrontech.com</a>
                </div>
                <div>
                  <div style={{ color: '#6d6880', fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Support phone</div>
                  <a href="tel:+13072178966" style={{ color: '#2dd4bf', textDecoration: 'none', fontWeight: 700, fontFamily: "'JetBrains Mono', Consolas, monospace", fontSize: 13 }}>+1 (307) 217-8966</a>
                </div>
                <div>
                  <div style={{ color: '#6d6880', fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Free intro call</div>
                  <a href="#book-call" style={{ color: '#facc15', textDecoration: 'none', fontWeight: 800, fontFamily: "'JetBrains Mono', Consolas, monospace", fontSize: 13 }}>Book a call</a>
                </div>
                <div>
                  <div style={{ color: '#6d6880', fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Registered address</div>
                  <div style={{ color: '#e8e6f0', fontSize: 12.5 }}>30 N Gould St, Ste N · Sheridan, WY 82801</div>
                </div>
              </div>
            </div>
            <div className="about-visual">
              <div className="tech-grid">
                <div className="tech-item">TypeScript</div>
                <div className="tech-item">Node.js</div>
                <div className="tech-item">React</div>
                <div className="tech-item">Kubernetes</div>
                <div className="tech-item">Docker</div>
                <div className="tech-item">Postgres</div>
                <div className="tech-item">MariaDB</div>
                <div className="tech-item">Stripe</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact — real business info for Stripe review + customers ── */}
      <section id="contact" className="contact-section">
        <div className="container">
          <span className="section-label">Contact</span>
          <h2>Reach the right desk.</h2>
          <p style={{ color: '#a8a4bc', maxWidth: 680, marginBottom: 28 }}>
            Three staffed inboxes plus direct phone support. No generic <code style={codeStyle}>info@</code> or <code style={codeStyle}>hello@</code> routing — messages go straight to the team that handles them. You can also{' '}
            <a href="#book-call" style={{ color: '#facc15', fontWeight: 800, textDecoration: 'none' }}>book a free call</a>.
          </p>

          <div id="book-call" className="booking-calendly-shell">
            <div className="booking-calendly-head">
              <div>
                <div style={bookingMetaStyle}>Calendly · bottleneck call</div>
                <h3 style={{ color: '#f8fafc', fontSize: 24, lineHeight: 1.15, margin: '5px 0 8px', letterSpacing: 0 }}>
                  Book your bottleneck call.
                </h3>
                <p style={bookingTextStyle}>
                  Bring the constraint: product scope, launch path, AI workflow, deployment,
                  Stripe, or a build that needs to get in front of real users.
                </p>
              </div>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" style={bookingButtonStyle}>
                Open Calendly
              </a>
            </div>
            <CalendlyInlineWidget />
          </div>

          {/* ── Primary contact grid — phone, owner, inboxes ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 14, marginBottom: 28,
          }}>
            <ContactCard color="#67e8f9" label="Support" email="support@zitotrontech.com"
              desc="Bugs · help · product questions · account issues · security [SECURITY]." />
            <ContactCard color="#4ade80" label="Sales" email="sales@zitotrontech.com"
              desc="Licensing · OEM · enterprise · custom · partnerships · acquisition." />
            <ContactCard color="#f87171" label="Legal" email="legal@zitotrontech.com"
              desc="License enforcement · IP · takedown · GDPR / CCPA data requests." />
            <ContactCard color="#c084fc" label="Owner" email="owner@zitotrontech.com"
              desc="Founder direct. Use for partnerships, acquisition, or when you've already tried sales@." />
          </div>

          {/* ── Phone + hours ── */}
          <div style={phoneBlock}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 26 }}>📞</div>
              <div>
                <div style={{ color: '#6d6880', fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>Direct support</div>
                <a href="tel:+13072178966" style={{
                  color: '#2dd4bf', fontSize: 22, fontWeight: 800, letterSpacing: 0.4,
                  textDecoration: 'none', textShadow: '0 0 8px rgba(45,212,191,0.5)',
                  fontFamily: "'JetBrains Mono', Consolas, monospace",
                }}>+1 (307) 217-8966</a>
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ color: '#a8a4bc', fontSize: 13, lineHeight: 1.6 }}>
                <div><strong style={{ color: '#e8e6f0' }}>Business hours:</strong> Mon–Fri, 9am–6pm Pacific</div>
                <div style={{ color: '#6d6880', fontSize: 11.5, marginTop: 2 }}>Email is typically faster. Acknowledged within 1 business day.</div>
              </div>
            </div>
          </div>

          {/* ── Legal / registered business info (required for Stripe review) ── */}
          <div style={legalBlock}>
            <div style={{ color: '#6d6880', fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 800, marginBottom: 10 }}>
              Registered business
            </div>
            <div style={legalGrid}>
              <LegalRow label="Entity"            value="Zitotron Tech LLC" />
              <LegalRow label="State of formation" value="Wyoming, USA" />
              <LegalRow label="Formation year"     value="2024" mono />
              <LegalRow label="Registered address" value="30 N Gould St, Ste N · Sheridan, WY 82801 · USA" />
              <LegalRow label="Operations HQ"      value="Portland, Oregon, USA" />
              <LegalRow label="D-U-N-S®"           value={<span className="sensitive-blur">133649238</span>} mono />
              <LegalRow label="Phone"              value="+1 (307) 217-8966" mono />
            </div>
          </div>

          {/* ── Refund policy summary — full text at /billing-policy ── */}
          <div style={refundBlock}>
            <div style={{ color: '#67e8f9', fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 800, marginBottom: 6 }}>
              Billing, refunds &amp; cancellation
            </div>
            <p style={{ color: '#a8a4bc', fontSize: 13.5, lineHeight: 1.6, margin: '0 0 8px' }}>
              Subscriptions (Solo, Founder, Startup, Enterprise) are billed monthly or annually per seat via Stripe. Subscription charges are <strong style={{ color: '#e8e6f0' }}>non-refundable</strong>. You can cancel or downgrade anytime from your{' '}
              <a href="https://agentomatic.io/account" style={{ color: '#67e8f9' }}>AgentOmatic.io account page</a> — the change takes effect at the end of the current billing period and you are <strong style={{ color: '#e8e6f0' }}>not billed again</strong>. You keep full access until then; no proration.
            </p>
            <Link to="/billing-policy" style={{
              display: 'inline-block',
              fontSize: 12, fontWeight: 700,
              color: '#67e8f9', textDecoration: 'none',
              borderBottom: '1px solid rgba(103,232,249,0.55)',
              paddingBottom: 1,
            }}>Read the full billing &amp; refund policy →</Link>
          </div>
        </div>
      </section>

      <ZitoFooter />

      {/* AgentOmatic.io product info modal — clean, formal, Stripe-safe
          description of the product. Opens from hero / highlight CTAs. */}
      {agentoOpen && <AgentoProductModal onClose={() => setAgentoOpen(false)} />}
    </div>
  );
};

// ─── AgentOmatic.io Product Modal ──────────────────────────────────────────
// Simple, professional product sheet — no marketing excess. Lists what
// the product is, what it does, how you buy it, and where to go next.
// Written for Stripe reviewers and buyers at the same time.

const AgentoProductModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 5000,
        background: 'radial-gradient(circle at 50% 40%, rgba(0,0,0,0.80) 0%, rgba(0,0,0,0.94) 100%)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div style={{
        maxWidth: 640, width: '100%',
        maxHeight: 'calc(100vh - 40px)',
        overflowY: 'auto',
        background: 'linear-gradient(145deg, #12131f 0%, #0f1019 100%)',
        border: '1px solid rgba(45,212,191,0.35)',
        borderRadius: 16,
        boxShadow: '0 0 40px rgba(45,212,191,0.18), 0 20px 80px rgba(0,0,0,0.6)',
        padding: '28px 30px 24px',
        position: 'relative',
      }}>
        <button onClick={onClose} aria-label="Close" style={{
          position: 'absolute', top: 14, right: 14,
          background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
          color: '#a8a4bc', width: 30, height: 30, borderRadius: 8,
          cursor: 'pointer', fontSize: 16, fontFamily: 'inherit',
        }}>×</button>

        <div style={{
          display: 'inline-block',
          fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
          color: '#2dd4bf', fontWeight: 800,
          background: 'rgba(45,212,191,0.10)',
          border: '1px solid rgba(45,212,191,0.35)',
          borderRadius: 999, padding: '3px 10px',
          marginBottom: 14,
        }}>Product</div>

        <h2 style={{
          fontSize: '1.75rem', fontWeight: 800, margin: '0 0 6px',
          background: 'linear-gradient(135deg, #2dd4bf 0%, #a78bfa 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>AgentOmatic.io</h2>
        <div style={{ color: '#a8a4bc', fontSize: 14, marginBottom: 18 }}>
          Cross-platform desktop software for software engineers.
        </div>

        <p style={{ color: '#a8a4bc', fontSize: 14.5, lineHeight: 1.65, margin: '0 0 16px' }}>
          AgentOmatic.io is a desktop application (macOS, Windows, Linux) that lets engineers
          run and coordinate multiple AI coding agents side-by-side in one workspace.
          It connects to your existing AI subscriptions and shows each agent's output,
          terminals, project files, and browser panels in a unified interface.
        </p>

        <div style={{
          background: 'rgba(10,11,18,0.6)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 10, padding: '14px 18px',
          margin: '16px 0',
        }}>
          <ModalRow label="Category"  value="Developer tool · Desktop software" />
          <ModalRow label="Platforms" value="macOS · Windows · Linux" />
          <ModalRow label="Delivery"  value="Download from AgentOmatic.io; installer is signed + notarized" />
          <ModalRow label="Licensing" value="Proprietary commercial (free Community tier + paid tiers + custom commercial)" />
          <ModalRow label="Customer" value="Professional software developers and engineering teams" />
        </div>

        <h3 style={{ color: '#e8e6f0', fontSize: 14, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase', margin: '18px 0 10px' }}>
          Plans &amp; billing
        </h3>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#a8a4bc', fontSize: 13.5, lineHeight: 1.75 }}>
          <li><strong style={{ color: '#22c55e' }}>Community</strong> — free account, basic scale, signup only.</li>
          <li><strong style={{ color: '#2dd4bf' }}>Solo</strong> — $25/month or $250/year per seat.</li>
          <li><strong style={{ color: '#c084fc' }}>Founder</strong> — $50/month or $500/year per seat.</li>
          <li><strong style={{ color: '#ec4899' }}>Startup</strong> — $200/month or $2,000/year per seat.</li>
          <li><strong style={{ color: '#fbbf24' }}>Enterprise</strong> — custom contract, contact sales.</li>
        </ul>
        <p style={{ color: '#6d6880', fontSize: 12, marginTop: 10, lineHeight: 1.6 }}>
          All paid plans are subscriptions billed via Stripe. Customers can upgrade, downgrade, or cancel anytime from their account page. Downgrades take effect at the end of the current billing period. No refunds on in-progress billing periods.
        </p>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
          <a href="https://agentomatic.io" target="_blank" rel="noopener noreferrer" className="btn-primary">
            Visit AgentOmatic.io →
          </a>
          <a href="https://agentomatic.io/#pricing" target="_blank" rel="noopener noreferrer" className="btn-secondary">
            See pricing
          </a>
        </div>
      </div>
    </div>
  );
};

const ModalRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
    <div style={{ color: '#6d6880', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 700, minWidth: 100 }}>{label}</div>
    <div style={{ color: '#e8e6f0', fontSize: 13, fontWeight: 500 }}>{value}</div>
  </div>
);

// ─── Sub-components ──────────────────────────────────────────────────────────

const ProductPosterCard: React.FC<{ product: ProductCard }> = ({ product }) => (
  <article className={`product-poster product-poster--${product.key}`}>
    <div className="product-poster-art" aria-hidden="true">
      <div className="product-poster-gridlines" />
      <div className="product-poster-core" />
      {product.icon === 'plus' && <div className="product-plus-icon" />}
      <div className="product-poster-ribbon" />
    </div>
    <div className="product-poster-content">
      <div className="product-status">{product.status}</div>
      <div>
        <div className="product-eyebrow">{product.eyebrow}</div>
        <h3>{product.title}</h3>
      </div>
      <p>{product.summary}</p>
      <ul>
        {product.bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
      {product.href ? <ProductPosterLink product={product} /> : (
        <span className="product-link product-link--muted">More details coming soon</span>
      )}
    </div>
  </article>
);

const ProductPosterLink: React.FC<{ product: ProductCard }> = ({ product }) => {
  const href = product.href ?? '#';
  const isInternal = href.startsWith('#');

  return (
    <a
      href={href}
      target={isInternal ? undefined : '_blank'}
      rel={isInternal ? undefined : 'noopener noreferrer'}
      className="product-link"
    >
      {product.cta ?? `Visit ${product.title}`}
    </a>
  );
};

const CalendlyInlineWidget: React.FC = () => {
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let retryId: number | undefined;

    const initialize = () => {
      if (cancelled || !widgetRef.current || !window.Calendly?.initInlineWidget) return;
      widgetRef.current.innerHTML = '';
      window.Calendly.initInlineWidget({
        url: CALENDLY_WIDGET_URL,
        parentElement: widgetRef.current,
      });
    };

    if (!document.getElementById(CALENDLY_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = CALENDLY_SCRIPT_ID;
      script.type = 'text/javascript';
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.addEventListener('load', initialize, { once: true });
      document.body.appendChild(script);
    } else {
      retryId = window.setInterval(() => {
        if (window.Calendly?.initInlineWidget) {
          if (retryId) window.clearInterval(retryId);
          initialize();
        }
      }, 120);
    }

    initialize();

    return () => {
      cancelled = true;
      if (retryId) window.clearInterval(retryId);
    };
  }, []);

  return (
    <div
      ref={widgetRef}
      className="calendly-inline-widget booking-calendly"
      data-url={CALENDLY_WIDGET_URL}
      aria-label="Book a bottleneck call"
    />
  );
};

const ContactCard: React.FC<{ color: string; email: string; label: string; desc: string; }> = ({ color, email, label, desc }) => (
  <a href={`mailto:${email}`} style={{
    display: 'block',
    padding: '16px 18px',
    background: 'linear-gradient(145deg, rgba(20,20,30,0.6) 0%, rgba(18,19,31,0.9) 100%)',
    border: `1px solid ${color}55`,
    borderRadius: 12,
    textDecoration: 'none',
    transition: 'transform 0.15s ease, border-color 0.2s ease, box-shadow 0.2s ease',
  }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.borderColor = color;
      e.currentTarget.style.boxShadow = `0 0 24px ${color}22`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = '';
      e.currentTarget.style.borderColor = `${color}55`;
      e.currentTarget.style.boxShadow = '';
    }}
  >
    <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color, fontWeight: 800, marginBottom: 6, textShadow: `0 0 6px ${color}55` }}>
      {label}
    </div>
    <div style={{ color, fontWeight: 700, fontSize: 13.5, textShadow: `0 0 4px ${color}40`, marginBottom: 6, wordBreak: 'break-all', fontFamily: "'JetBrains Mono', Consolas, monospace" }}>
      {email}
    </div>
    <div style={{ color: '#a8a4bc', fontSize: 11.5, lineHeight: 1.55 }}>{desc}</div>
  </a>
);

const LegalRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
    <div style={{ color: '#6d6880', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 700, minWidth: 160 }}>{label}</div>
    <div style={{
      color: '#e8e6f0', fontSize: 13.5, fontWeight: 600,
      fontFamily: mono ? "'JetBrains Mono', Consolas, monospace" : 'inherit',
    }}>{value}</div>
  </div>
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const codeStyle: React.CSSProperties = {
  margin: '0 3px', padding: '1px 6px',
  background: 'rgba(167,139,250,0.10)',
  border: '1px solid rgba(167,139,250,0.25)',
  borderRadius: 4, fontSize: '0.88em',
};
const phoneBlock: React.CSSProperties = {
  padding: '18px 22px',
  background: 'linear-gradient(135deg, rgba(45,212,191,0.05) 0%, rgba(167,139,250,0.05) 100%)',
  border: '1px solid rgba(45,212,191,0.30)',
  borderRadius: 12,
  marginBottom: 20,
};
const bookingMetaStyle: React.CSSProperties = {
  color: '#facc15',
  fontSize: 10.5,
  letterSpacing: 2,
  textTransform: 'uppercase',
  fontWeight: 800,
};
const bookingTextStyle: React.CSSProperties = {
  color: '#a8a4bc',
  fontSize: 13.5,
  lineHeight: 1.6,
  margin: 0,
  maxWidth: 620,
};
const bookingButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 42,
  padding: '0 18px',
  background: 'linear-gradient(135deg, #facc15 0%, #2dd4bf 100%)',
  color: '#081014',
  borderRadius: 8,
  textDecoration: 'none',
  fontWeight: 900,
  fontSize: 13,
  whiteSpace: 'nowrap',
  boxShadow: '0 12px 28px rgba(250,204,21,0.16)',
};
const legalBlock: React.CSSProperties = {
  padding: '20px 22px',
  background: 'linear-gradient(145deg, rgba(20,20,30,0.6) 0%, rgba(18,19,31,0.9) 100%)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 12,
  marginBottom: 20,
};
const legalGrid: React.CSSProperties = { display: 'flex', flexDirection: 'column' };
const refundBlock: React.CSSProperties = {
  padding: '16px 20px',
  background: 'rgba(103,232,249,0.04)',
  border: '1px solid rgba(103,232,249,0.30)',
  borderRadius: 12,
};

export default HomePage;
